import type { DocPageContent } from '../docs-content'

const page = {
  order: 11,
  slug: 'text-captions-subtitles',
  title: '文本、字幕与副标题',
  description:
    '添加并美化文本片段、从转录稿生成字幕，以及导入内嵌副标题轨。',
  category: '创意工具',
  related: ['motion-library', 'motion', 'shapes-masks', 'properties', 'local-ai'],
  sections: [
    {
      title: '三种相关概念',
      blocks: [
        {
          kind: 'table',
          headers: ['类型', '是什么', '来源'],
          rows: [
            ['文本片段', '一个可输入内容的独立时间轴项', '文本标签页'],
            ['字幕', '附着在视频或音频片段上的生成式文本行', '片段转录稿'],
            ['副标题', '时间轴上的提示点轨', '内嵌轨，或合并后的字幕'],
          ],
        },
      ],
    },
    {
      title: '添加并美化文本片段',
      blocks: [
        {
          kind: 'steps',
          items: [
            '打开**文本**标签页，然后点击**添加文本**或某个模板，将文本放置到播放头处——或者将模板拖到时间轴或画布上。',
            '选择一种版式（**单段**、**两段**、**三段**）以及一种样式预设，例如 Clean、Lower Third、Cinematic 或 Neon。',
            '在**文本**部分设置字体、大小（8–500 px）、字重、颜色、背景、对齐、间距、行高、内边距和圆角。',
            '在**特效**下添加阴影或描边，并在**文本动画**下为文本添加动画。',
          ],
        },
      ],
    },
    {
      title: '文本动画',
      blocks: [
        {
          kind: 'paragraph',
          text: '在**文本动画**部分（位于文本属性或所选图层的**运动**库中），每个字符、单词或行都可以独立动画。文本动画始终保持实时与程序化，因此无需管理图层关键帧。',
        },
        {
          kind: 'steps',
          items: [
            '选中一个文本片段并打开**文本动画**部分。',
            '在**入**、**出**或**循环**行中选择一个预设。**入**在片段开头播放，**出**在片段结尾播放，**循环**在两者之间持续运行。',
            '再次点击当前预设（或其 ✕）即可移除——每行仅一个预设。',
            '微调当前预设下方出现的控件。',
            '在运动中，拖动文本运动带的边缘可更改其时长，或将入带或出带拖离片段边缘以产生偏移。',
          ],
        },
        {
          kind: 'table',
          headers: ['控件', '作用'],
          rows: [
            ['作用于', '每个预设动画的单位：字符、单词、行或整个片段。'],
            ['时长', '每个单位动画的长度，以帧计。'],
            ['错开', '每个单位之间的延迟帧数，用于产生级联效果。'],
            ['顺序', '哪个单位先动画：向前、向后、从中心向外或随机。'],
            ['强度', '缩放效果强度，范围 0 到 200%。'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '**已应用**列表显示每个实时的文本运动槽位。选择一个条目可将播放头移动到其时间轴带。',
        },
      ],
    },
    {
      title: '从转录稿生成字幕',
      blocks: [
        {
          kind: 'steps',
          items: [
            '从媒体库或转录稿面板为片段生成转录稿。',
            '在片段右键菜单中使用**生成字幕**（或让精剪自动启用转录稿字幕）。',
            '在**副标题**部分编辑提示点时序与文本，并选择一种样式预设（Netflix、YouTube、Bold Yellow、Outlined、TikTok）。',
            '调整字幕颜色、大小、垂直位置，以及可选的背景。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '使用**将字幕合并为片段**可将转录稿字幕转换为独立的副标题片段。',
        },
      ],
    },
    {
      title: '内嵌与导入的副标题',
      blocks: [
        {
          kind: 'list',
          items: [
            '在媒体卡片上使用**提取内嵌副标题**来扫描文件中的副标题轨。',
            '在**内嵌副标题**对话框中，选择一条轨道——每一行显示其语言、编解码器和提示点数量。',
            '插入该轨道可将其提示点作为副标题片段添加到时间轴。',
            '在**副标题**部分编辑每个提示点的开始、结束与文本，并设置整条提示点的斜体、粗体或下划线。',
          ],
        },
      ],
    },
    {
      title: '导出时的副标题',
      blocks: [
        {
          kind: 'note',
          tone: 'info',
          text: '在导出设置中开启**嵌入副标题**，即可将转录稿字幕作为副标题轨包含进去（MP4、MKV、WebM）。某些播放器默认隐藏副标题轨，因此观众可能需要手动开启。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
